
import React from 'react';
import { FaBook } from 'react-icons/fa';
import useTitle from '../hooks/useTitle';

const BASE_URL = 'http://172.233.20.19:3300/api';

const ApiDocsPage = () => {
  useTitle("MENELIK - API");
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold mb-6 flex items-center text-gray-800">
        <FaBook className="mr-2 text-blue-600" />
        API Documentation
      </h1>

      {/* Introduction */}
      <div className="mb-8">
        <p className="text-gray-700">
          Below you will find the available endpoints at <span className="font-mono">{BASE_URL}</span>. 
          Each endpoint returns data in JSON format.
        </p>
      </div>

      {/* 1. /transactions/:address */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          1. GET <span className="font-mono">/transactions/:address</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Retrieves transactions associated with a given address. Uses pagination with a page size of 100 records.
        </p>
        <div className="mb-2">
          <strong>Usage example:</strong>
          <div className="bg-gray-100 p-2 rounded text-sm mt-1">
            <code>
              {`${BASE_URL}/transactions/MdpodeqzPV2Cg59EpkFvjrYek4rk6ytg8l?page=2`}
            </code>
          </div>
        </div>
        <p className="text-gray-700 mb-1"><strong>Parameters:</strong></p>
        <ul className="list-disc ml-6 text-gray-700 mb-2">
          <li><strong>address</strong> (required): The address to query.</li>
          <li><strong>page</strong> (optional): Page number (default 1).</li>
        </ul>
        <p className="text-gray-700 mb-1"><strong>Response format:</strong></p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "page": 2,
  "results": [
    {
      "tx_hash": "abc123...",
      "amount": "10.00000000",
      "direction": "RECEIVED",
      "balance": "100.00000000",
      "timestamp": "Sep 20, 2025 09:15:22 AM UTC"
    },
    ...
  ]
}`}
        </pre>
      </section>

      {/* 2. /top-addresses */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          2. GET <span className="font-mono">/top-addresses</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns the top 200 addresses, ordered by balance and by total received.
        </p>
        <div className="mb-2">
          <strong>Usage example:</strong>
          <div className="bg-gray-100 p-2 rounded text-sm mt-1">
            <code>
              {`${BASE_URL}/top-addresses`}
            </code>
          </div>
        </div>
        <p className="text-gray-700 mb-1"><strong>Response format:</strong></p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "topBalances": [
    {
      "address": "MKP75PpU1gMc2smtFe4c7b323s14TDBYU9",
      "balance": "736105698.37333593",
      "total_received": "1441886163.54557679",
      "total_sent": "705780465.17224086",
      "last_updated": "2025-01-19T14:44:01.000Z",
      "display_name": null
    },
    ...
  ],
  "topReceived": [
    {
      "address": "MPFBvaxVjC6UK4WA1ijTU1SYQ45R5rhg6B",
      "total_received": "289849917.99596892",
      "balance": "196259950.99958036",
      "total_sent": "93589966.99638856",
      "last_updated": "2024-11-17T19:35:12.000Z",
      "display_name": "MyClaimedAddress"
    },
    ...
  ]
}`}
        </pre>
      </section>

      {/* 3. /address/:address */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          3. GET <span className="font-mono">/address/:address</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns basic information (balance, total_received, total_sent) for a specific address in the <span className="font-mono">addresses</span> table.
        </p>
        <div className="mb-2">
          <strong>Usage example:</strong>
          <div className="bg-gray-100 p-2 rounded text-sm mt-1">
            <code>
              {`${BASE_URL}/address/MPFBvaxVjC6UK4WA1ijTU1SYQ45R5rhg6B`}
            </code>
          </div>
        </div>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`[
  {
    "address": "MPFBvaxVjC6UK4WA1ijTU1SYQ45R5rhg6B",
    "total_received": "270782003.06629523",
    "total_sent": "266716003.06089433",
    "balance": "4066000.00540090",
    "last_updated": "2025-01-19T13:08:44.000Z"
  }
]`}
        </pre>
      </section>

      {/* 4. /table-records */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          4. GET <span className="font-mono">/table-records</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns the total record counts for main tables (<span className="font-mono">addresses, blocks, transactions</span>, etc.) and stats for the last 14 days of transactions, plus difficulty data for the last 720 blocks.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "addresses": 100000,
  "blocks": 120000,
  "transactions": 215000,
  "transaction_inputs": 380000,
  "transaction_outputs": 420000,
  "transactions_last_14_days": {
    "points": [
      { "date": "2025-09-10", "count": 126 },
      { "date": "2025-09-11", "count": 135 },
      ...
    ],
    "total_transactions": 1820
  },
  "difficulty": [
    { "height": 451000, "difficulty": 123456.789, "hashrate": 1.234e+10 },
    ...
  ]
}`}
        </pre>
      </section>

      {/* 5. /last-blocks */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          5. GET <span className="font-mono">/last-blocks</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Retrieves up to 440 records from the <span className="font-mono">latest_blocks</span> table (height, hash, nTx, etc.).
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`[
  {
    "height": 339700,
    "block_hash": "00000000000abc123...",
    "nTx": 6,
    "confirmations": 12,
    "difficulty": 12345.6789,
    "size": 892,
    "time": "2025-09-19T12:34:56.000Z"
  },
  ...
]`}
        </pre>
      </section>

      {/* 6. /total-emitted */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          6. GET <span className="font-mono">/total-emitted</span>
        </h2>
        <p className="text-gray-700 mb-2">
          A quick query for total emitted coins, based on the sum of all balances in the <span className="font-mono">addresses</span> table.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "totalEmitted": 1702702880
}`}
        </pre>
      </section>

      {/* 7. /txid/:txid */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          7. GET <span className="font-mono">/txid/:txid</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Detailed information about a transaction (64-char hex <strong>txid</strong>).
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "txid": "abc123...",
  "decrypt": 1,
  "transaction": {
    "txid": "abc123...",
    "block_hash": "00000000000abc...",
    "block_height": 339700,
    "block_time": "2025-09-20T10:12:34.000Z",
    ...
    "vin": [
      { "previous_txid": "...", "vout": 1, ... },
      ...
    ],
    "vout": [
      {
        "value": "25.00000000",
        "n": 0,
        "scriptPubKey": {
          "asm": "OP_DUP OP_HASH160 ...",
          "addresses": ["FK..."]
        }
      },
      ...
    ]
  }
}`}
        </pre>
      </section>

      {/* 8. /ext/tx/:txid */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          8. GET <span className="font-mono">/ext/tx/:txid</span>
        </h2>
        <p className="text-gray-700 mb-2">
          A simplified version returning <strong>input_addresses</strong> and <strong>recipients</strong> for a transaction.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "transaction_id": "abc123...",
  "input_addresses": [
    { "address": "FK123...", "value": "10.00000000" },
    ...
  ],
  "recipients": [
    { "address": "FG456...", "value": "9.99990000" },
    ...
  ]
}`}
        </pre>
      </section>

      {/* 9. /latest-blocks-summary */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          9. GET <span className="font-mono">/latest-blocks-summary</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Shows a summary of the latest blocks (stored in <span className="font-mono">latest_blocks_summary</span> table).
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`[
  {
    "Block": 339700,
    "BlockHash": "00000000000abc123...",
    "Time": "2025-09-21 08:42:10",
    "TxCount": 8
  },
  ...
]`}
        </pre>
      </section>

      {/* 10. /block-height/:height & /block-hash/:hash */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          10. GET <span className="font-mono">/block-height/:height</span> & <span className="font-mono">/block-hash/:hash</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns block info by height or by block hash.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "block_hash": "00000000000abc123...",
  "height": 339700,
  "time": 1737128331,
  "previousblockhash": "...",
  "nextblockhash": "...",
  "transactions": [
    {
      "txid": "abcd1234...",
      "recipients": [
        { "address": "F...", "value": "10.0" },
        ...
      ]
    },
    ...
  ]
}`}
        </pre>
      </section>

      {/* 11. /block-details/:block_hash */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          11. GET <span className="font-mono">/block-details/:block_hash</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns more detailed information about a block, including <strong>inputs</strong> and <strong>outputs</strong> of each transaction.
        </p>
      </section>

      {/* 12. /getdifficulty, /getconnectioncount, /getpeerinfo, /getnetworkhashps, /getblockcount */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          12. RPC Stats
        </h2>
        <ul className="list-disc ml-6 text-gray-700 mb-2">
          <li><span className="font-mono">/getdifficulty</span>: Current network difficulty.</li>
          <li><span className="font-mono">/getconnectioncount</span>: Number of active peer connections.</li>
          <li><span className="font-mono">/getpeerinfo</span>: Peer details (address, version, etc.).</li>
          <li><span className="font-mono">/getnetworkhashps</span>: Estimated network hashrate.</li>
          <li><span className="font-mono">/getblockcount</span>: Current block height.</li>
        </ul>
      </section>

      {/* 13. /getNetworkStats */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          13. GET <span className="font-mono">/getNetworkStats</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns various network stats in a single JSON, including difficulty, connection count, hashrate, block count, and more (using <strong>gettxoutsetinfo</strong> for total emitted).
        </p>
      </section>

      {/* 14. /supply */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          14. GET <span className="font-mono">/supply</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Shows data on maximum supply, circulating, burnt, developer coins, etc.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "maximumSupply": 21000000000,
  "totalEmitted": 1713447880,
  "burntCoins": 1673349.42222665,
  "developerCoins": 142842723.201517,
  "circulatingSupply": "1568931807.37625623",
  "percentages": {
    "burnt": "0.01%",
    "emitted": "8.16%",
    "pendingToEmit": "92.53%"
  },
  "burnAddress": "MPFBvaxVjC6UK4WA1ijTU1SYQ45R5rhg6B",
  "developerAddress": "MhjBvaxVjC6UK4WA1ijTU1SYQ45R5fdhn6k"
}`}
        </pre>
      </section>

      {/* 15. /getSmartnodeLockedCoins */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          15. GET <span className="font-mono">/getSmartnodeLockedCoins</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Calculates addresses holding valid Smartnode collateral and the total amount of locked coins.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "totalLockedCoins": "537950000.00000000",
  "lockedAddressesCount": 293,
  "addresses": [
    {
      "address": "MPFBvaxVjC6UK4WA1ijTU1SYQ45R5rhg6B",
      "balance": "700000.00000000"
    },
    ...
  ]
}`}
        </pre>
      </section>

      {/* 16. /getSmartnodeData */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          16. GET <span className="font-mono">/getSmartnodeData</span>
        </h2>
        <p className="text-gray-700 mb-2">
          Returns Smartnode data (summary and list) if <strong>?includeSummary=true&amp;includeList=true</strong> are provided.
        </p>
        <pre className="bg-gray-100 p-2 rounded text-sm overflow-auto">
{`{
  "summary": {
    "totalNodes": 366,
    "enabled": 293,
    "unknown": 73,
    "otherStatuses": 0
  },
  "list": {
    "a3272278ab93b5a...": {
      "address": "148.171.140.18:2023",
      "payee": "MpeqcX7bv38iweNLZ2A32MhobP4Htfs6jp",
      "status": "ENABLED",
      ...
    },
    ...
  }
}`}
        </pre>
      </section>

      {/* Footer / Note */}
      <div className="mt-12 pt-4 border-t border-gray-300 text-gray-600 text-sm">
        <p>
          You can access each route via <span className="font-mono">{BASE_URL}/...</span>. 
          Make sure to check additional parameters like <strong>page</strong> if needed.
        </p>
      </div>
    </div>
  );
};

export default ApiDocsPage;
